define('salesInboxDetailView',[
  'module',
  'app',
  'marionette',
  'backbone',
  'underscore',
  'jquery',
  'settings',
  'entities/sales_inbox',
  'entities/threads',
  'entities/admin/user_groups',
  'entities/customer_commands',
  'entities/customers/customers',
  'entities/companies/companies',
  'salesInboxCommandsPanelView',
  'secondScreenBridge'
], function (Module, App, Marionette, Backbone, _, $, Settings,
             /* entities auto-register on load */ SalesInboxEntities, ThreadsEntities,
             UserGroupsEntities, CustomerCommandsEntities, CustomersEntities, CompaniesEntities,
             CommandsPanelView, SecondScreen) {
  'use strict';

  var CATEGORIES = ['SALES', 'DESIGN', 'INVOICING', 'RECLAMATION', 'TRACKING', 'NOT_ACTIONABLE'];
  var CASES = {
    SALES:          ['PRICE_NEGOTIATION', 'OFFER_DECLINED', 'EXPRESS_DELIVERY', 'STOCKS', 'PURCHASE_ORDER', 'REORDER', 'READY_TO_ORDER', 'DELIVERY_TIME', 'SAMPLE', 'QUANTITY', 'OFFER_REQUEST', 'CHANGE', 'UNFINISHED_ORDER', 'UNDER_REVIEW'],
    DESIGN:         ['ASK_FOR_MODIFICATION', 'LOGO_NOT_PRINTABLE', 'NO_LOGO_SENT'],
    INVOICING:      ['ASK_FOR_INVOICE', 'PAY_AFTER_DELIVERY', 'CHANGE_DATA', 'ASK_FOR_RIB'],
    RECLAMATION:    ['PRINTING_ISSUE', 'BROKEN_GOODS', 'MISSING_GOODS'],
    TRACKING:       ['WHEN_WILL_ARRIVE', 'DID_NOT_ARRIVE', 'PROBLEM'],
    NOT_ACTIONABLE: ['OUT_OF_OFFICE', 'EMAIL_NOT_DELIVERED', 'CARRIER_SURVEY', 'VENDOR_SOLICITATION']
  };
  var POTENTIALS = ['SMALL_ORDER_VALUE', 'MEDIUM_ORDER_VALUE', 'BIG_ORDER_VALUE'];
  var LOCALES = ['de', 'fr', 'nl', 'it', 'es', 'da', 'en'];
  var STATUSES = ['NEW', 'AWAITING', 'RESOLVED'];

  // Prev/next navigation walks the same ordered list the user was viewing, so
  // it reads the list's persisted filters (written by the list view under this
  // key) and fetches that filtered/ordered list to locate the current item.
  var LIST_FILTER_KEY = 'salesInbox.listFilters';
  // Window of items fetched to resolve neighbours — generous enough to walk a
  // long way without refetching, bounded so the lookup stays cheap.
  var NAV_WINDOW = 200;
  // Module-scoped cache of the ordered secId list keyed by a signature of the
  // active filters, shared across detail view instances so each next/prev hop
  // (which recreates the view) doesn't refetch the list.
  var navCache = { sig: null, ids: null };

  function readListFilters() {
    try {
      var raw = window.localStorage && window.localStorage.getItem(LIST_FILTER_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  }

  // ---- Activity timeline helpers --------------------------------------
  // Parse a JSON blob from the backend into a plain object (backend writes
  // its own minimal JSON; we tolerate invalid payloads by returning empty).
  function parseActivityDetails(raw) {
    if (!raw) { return {}; }
    if (typeof raw === 'object') { return raw; }
    try { return JSON.parse(raw); } catch (e) { return {}; }
  }

  // Short relative-time strings: "2 min ago", "5 h ago", "3 d ago".
  function relativeTime(date) {
    if (!date) { return ''; }
    var diffMs = Date.now() - date.getTime();
    var min = Math.round(diffMs / 60000);
    if (min < 1)     { return 'just now'; }
    if (min < 60)    { return min + ' min ago'; }
    var hr = Math.round(min / 60);
    if (hr  < 24)    { return hr + ' h ago'; }
    var day = Math.round(hr / 24);
    if (day < 30)    { return day + ' d ago'; }
    var mo = Math.round(day / 30);
    if (mo  < 12)    { return mo + ' mo ago'; }
    return Math.round(mo / 12) + ' y ago';
  }

  function escapeHtml(s) {
    var str = (s === null || s === undefined) ? '' : String(s);
    return str
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function strong(s) { return '<strong>' + escapeHtml(s) + '</strong>'; }
  function prettyCase(s) {
    if (!s) { return ''; }
    return String(s).replace(/_/g, ' ').toLowerCase().replace(/\b([a-z])/g, function (_, c) { return c.toUpperCase(); });
  }

  // ---- SLA / priority helpers (shared visual language with the list) ----
  // Priority badge tier — four bands sharing the urgency ramp.
  function prioTier(score) {
    var s = Number(score) || 0;
    if (s >= 13) { return 'max'; }
    if (s >= 6)  { return 'hi'; }
    if (s >= 3)  { return 'mid'; }
    return 'lo';
  }

  // Short "time remaining" label, e.g. "1h 12m", "9h", "1d 4h".
  function leftLabel(remH) {
    if (remH >= 24) {
      var d = Math.floor(remH / 24);
      return d + 'd ' + Math.round(remH - d * 24) + 'h';
    }
    var h = Math.floor(remH);
    var m = Math.round((remH - h) * 60);
    if (h >= 10) { return h + 'h'; }
    if (h >= 1)  { return h + 'h ' + (m < 10 ? '0' : '') + m + 'm'; }
    return Math.max(1, Math.round(remH * 60)) + 'm';
  }

  function overdueLabel(overH) {
    if (overH >= 24) { return Math.floor(overH / 24) + 'd over'; }
    if (overH >= 1)  { return Math.round(overH) + 'h over'; }
    return Math.round(overH * 60) + 'm over';
  }

  // Compute the value-weighted SLA state for the header burn gauge: the
  // colour band, the elapsed % (burn fill), and a countdown label. The SLA
  // window scales with order value (BIG 2h / MEDIUM 8h / else 24h).
  function slaGauge(receivedAt, potential) {
    if (!receivedAt) { return { cls: 'calm', pct: 0, word: 'left', label: '—' }; }
    var ageH = (Date.now() - new Date(receivedAt).getTime()) / 36e5;
    if (isNaN(ageH)) { return { cls: 'calm', pct: 0, word: 'left', label: '—' }; }
    var slaH = potential === 'BIG_ORDER_VALUE' ? 2
             : potential === 'MEDIUM_ORDER_VALUE' ? 8 : 24;
    var rem = slaH - ageH;
    var cls;
    if (rem <= 0) { cls = 'hot'; }
    else { var r = rem / slaH; cls = r <= 0.34 ? 'hot' : r <= 0.67 ? 'warn' : 'calm'; }
    return {
      cls: cls,
      pct: Math.max(0, Math.min(100, (ageH / slaH) * 100)),
      word: rem <= 0 ? 'breach' : 'left',
      label: rem <= 0 ? overdueLabel(-rem) : leftLabel(rem)
    };
  }

  // Crisp monochrome glyphs for the activity timeline (replaces the emoji
  // set — steadier in a money tool). Falls back to a neutral dot.
  var KIND_GLYPH = {
    classified: '●',    // ●
    reclassified: '✎',  // ✎
    assigned: '▸',      // ▸
    reassigned: '↪',    // ↪
    status: '↻',        // ↻
    sla: '▲',           // ▲
    note: '“',          // “
    replied: '✉',       // ✉
    viewed: '○',        // ○
    generic: '·'        // ·
  };

  // Map each EmailActivityAction to {kind, icon, title, subtitle} so the UI
  // renders a readable sentence instead of a JSON blob. Everything that
  // produces HTML uses escapeHtml / strong() — any text coming from user
  // input (notes) goes through escapeHtml.
  function summariseActivity(e) {
    var d = e._parsed || {};
    switch (e.action) {
      case 'CLASSIFIED': {
        var parts = [];
        if (d.category) { parts.push(strong(prettyCase(d.category))); }
        if (d.emailCase) { parts.push(prettyCase(d.emailCase)); }
        var title = parts.length ? 'Classified as ' + parts.join(' · ') : 'Classified';
        var sub = [];
        if (d.potential)      { sub.push(d.potential === 'BIG_ORDER_VALUE' ? 'VIP' : prettyCase(d.potential)); }
        if (Number(d.potentialValue) > 0) { sub.push('€' + Math.round(Number(d.potentialValue))); }
        if (d.priorityScore)  { sub.push('prio ' + d.priorityScore); }
        if (typeof d.confidence === 'number') { sub.push(Math.round(d.confidence * 100) + '% sure'); }
        if (d.source)         { sub.push('via ' + d.source); }
        if (d.noLinkedCustomer) { sub.push('⚠ no linked customer'); }
        return { kind: 'classified', icon: '🏷', title: title, subtitle: sub.join(' · ') };
      }
      case 'ASSIGNED': {
        var name = d.userpidName || (d.userpidSecId ? '' : 'a user');
        return {
          kind: 'assigned',
          icon: '👤',
          title: 'Assigned to ' + strong(name || '—'),
          subtitle: d.group ? 'Team ' + escapeHtml(d.group) : ''
        };
      }
      case 'REASSIGNED': {
        var from = d.fromUserpidName || (d.fromUserpidSecId ? '—' : 'Unassigned');
        var to   = d.toUserpidName   || (d.toUserpidSecId   ? '—' : 'Unassigned');
        var t = 'Reassigned from ' + strong(from) + ' to ' + strong(to);
        var reason = d.reason === 'status_changed' ? 'took the item after status change' : null;
        return { kind: 'reassigned', icon: '↪', title: t, subtitle: reason || '' };
      }
      case 'STATUS_CHANGED': {
        if (d.slaBreached) {
          var age = d.ageMinutes ? Math.round(Number(d.ageMinutes) / 60) + ' h' : 'overdue';
          return {
            kind: 'sla',
            icon: '⚠',
            title: 'SLA breach',
            subtitle: age + (d.slaHours ? ' (limit ' + d.slaHours + ' h)' : '')
          };
        }
        var a = prettyCase(d.from || 'New');
        var b = prettyCase(d.to   || '—');
        return { kind: 'status', icon: '🔄', title: 'Status ' + strong(a) + ' → ' + strong(b), subtitle: '' };
      }
      case 'MANUAL_RECLASSIFIED': {
        var bits = [];
        if (d.toCategory)  { bits.push(prettyCase(d.toCategory)); }
        if (d.toCase)      { bits.push(prettyCase(d.toCase)); }
        if (d.toPotential) { bits.push(d.toPotential === 'BIG_ORDER_VALUE' ? 'VIP' : prettyCase(d.toPotential)); }
        return {
          kind: 'reclassified',
          icon: '✏',
          title: 'Reclassified',
          subtitle: bits.length ? bits.join(' · ') : ''
        };
      }
      case 'NOTE_ADDED': {
        var note = d.note || '';
        if (note.length > 140) { note = note.slice(0, 140) + '…'; }
        return { kind: 'note', icon: '💬', title: 'Added a note', subtitle: escapeHtml(note) };
      }
      case 'REPLIED':
        return { kind: 'replied', icon: '✉', title: 'Replied', subtitle: '' };
      case 'VIEWED':
        return { kind: 'viewed', icon: '👁', title: 'Viewed', subtitle: '' };
      default:
        return { kind: 'generic', icon: '•', title: prettyCase(e.action || 'Event'), subtitle: '' };
    }
  }

  Module.exports = Marionette.LayoutView.extend({
    className: 'sales-inbox-detail-view',
    template: _.template(
      '<div class="detail-header">' +
      '  <div class="cmdbar">' +
      '    <a class="qpos" href="#sales-inbox" title="Back to the queue" aria-label="Back to the queue">' +
      '      <span class="chev" aria-hidden="true">&lsaquo;</span>' +
      '      <span class="pos-wrap">Queue <span class="pos"></span></span>' +
      '    </a>' +
      '    <div class="cmd-id">' +
      '      <span class="prio p-<%= prioTier %>" title="Priority <%= priorityScore %>"><%= priorityScore %></span>' +
      '      <div class="meta">' +
      '        <div class="subjline"><span class="subj"><%= subject || emailMessageIdHeader || "(no subject)" %></span></div>' +
      '        <div class="custline">' +
      '          <% if (customerVip) { %><span class="vip" title="VIP customer">VIP</span><% } %>' +
      '          <span class="cust"><%= customerName || "Unknown customer" %></span>' +
      '          <% if (senderEmail) { %><span class="email">&lt;<%= senderEmail %>&gt;</span>' +
      '            <button type="button" class="btn-copy-email" title="Copy email address" aria-label="Copy email address" data-email="<%= senderEmail %>">⧉</button><% } %>' +
      '          <% if (receivedAt) { %><span class="sep">·</span><span class="when">received <%= receivedLabel %></span><% } %>' +
      '        </div>' +
      '      </div>' +
      '    </div>' +
      '    <div class="cmd-right">' +
      '      <div class="nav-pair">' +
      '        <button class="btn btn-nav btn-prev" disabled title="Previous item (K)" aria-label="Previous item">&lsaquo;</button>' +
      '        <button class="btn btn-nav btn-next" disabled title="Next item (J)" aria-label="Next item">&rsaquo;</button>' +
      '      </div>' +
      '      <div class="countdown s-<%= sla.cls %>" title="SLA window scales with order value">' +
      '        <span class="lab">SLA <%= sla.word %></span><span class="t"><%= sla.label %></span>' +
      '      </div>' +
      '      <select class="q-status" title="Change status">' +
      '        <% _.each(statuses, function(s) { %>' +
      '          <option value="<%= s %>" <%= status === s ? "selected" : "" %>><%= prettyStatus(s) %></option>' +
      '        <% }); %>' +
      '      </select>' +
      '      <button class="btn btn-open-email" disabled title="Open the email in a larger popup">Open ↗</button>' +
      '      <button class="btn primary btn-reply" title="Reply (R)">Reply</button>' +
      '    </div>' +
      '  </div>' +
      '  <div class="burn s-<%= sla.cls %>"><div class="fill" style="width:<%= sla.pct %>%"></div></div>' +
      '  <div class="metastrip">' +
      '    <button type="button" class="chip team btn-change-team" title="Change team">' +
      '      <span class="k">Team</span> <%= assignedGroupName || "No team" %> <span class="pencil" aria-hidden="true">✎</span>' +
      '    </button>' +
      '    <button type="button" class="chip assignee btn-change-assignee" title="Change assignee">' +
      '      <span class="k">Assignee</span> <%= assignedUserpidName || "Unassigned" %> <span class="pencil" aria-hidden="true">✎</span>' +
      '    </button>' +
      '    <span class="chip value"><span class="k">Value</span> €<%= Math.round(Number(potentialValue) || 0) %> potential</span>' +
      '    <% if (manualOverride) { %><span class="chip manual">manual</span><% } %>' +
      '    <% if (classificationConfidence != null) { %>' +
      '      <span class="confidence">classified <%= Math.round(classificationConfidence * 100) %>%' +
      '        <span class="confbar"><i style="width:<%= Math.round(classificationConfidence * 100) %>%"></i></span>' +
      '      </span>' +
      '    <% } %>' +
      '  </div>' +
      '</div>' +

      '<div class="detail-body">' +
      '  <button type="button" class="mail-search-toggle" title="Search the mailbox for this customer’s emails" aria-label="Search customer emails">' +
      '    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>' +
      '  </button>' +
      '  <div class="mail-search-panel"></div>' +
      '  <div class="main-col">' +
      '    <div class="thread-panel"></div>' +
      '  </div>' +
      '  <div class="side-col">' +
      '    <div class="thread-items-panel"></div>' +
      '    <div class="company-panel"></div>' +
      '    <div class="commands-panel"></div>' +
      '    <div class="classification-panel"></div>' +
      '    <div class="activity-panel"></div>' +
      '  </div>' +
      '</div>'
    ),

    regions: {
      thread: '.thread-panel',
      commands: '.commands-panel',
      classification: '.classification-panel',
      activity: '.activity-panel',
      company: '.company-panel',
      threadItems: '.thread-items-panel',
      mailSearch: '.mail-search-panel'
    },

    events: {
      'change .q-status': 'onQuickStatus',
      'click .btn-reply': 'onReply',
      'click .btn-open-email': 'onOpenEmailPopup',
      'click .mail-search-toggle': 'onToggleMailSearch',
      'click .btn-prev': 'onPrev',
      'click .btn-next': 'onNext',
      'click .btn-change-assignee': 'onChangeAssignee',
      'click .btn-change-team': 'onChangeTeam',
      'click .btn-copy-email': 'onCopyEmail'
    },

    initialize: function (options) {
      this.secId = options.secId;
      this.prevSecId = null;
      this.nextSecId = null;
    },

    // --- Second-screen shortcuts -----------------------------------------
    // While this item is open, Alt+1/2/3 push the linked customer's board /
    // profile to the dashboard's OTHER window (the rep's second screen). We
    // bind on document (not the view's el, which never holds focus) and clean
    // up on destroy. Uses e.code so it still fires on macOS, where Option+digit
    // would otherwise resolve e.key to a special character.
    onShow: function () {
      // Queue navigation: J/K walk next/previous, R starts a reply. Bound on
      // document (the view's el never holds focus); ignored while typing.
      this._onNavKey = _.bind(this.onNavKey, this);
      $(document).on('keydown.salesInboxNav', this._onNavKey);

      // Second-screen shortcuts are an advanced-mode-only feature.
      if (Settings.configValue('advancedMode') !== true) { return; }
      this._onShortcutKey = _.bind(this.onShortcutKey, this);
      $(document).on('keydown.salesInboxSecondScreen', this._onShortcutKey);
    },

    onBeforeDestroy: function () {
      $(document).off('keydown.salesInboxNav');
      $(document).off('keydown.salesInboxSecondScreen');
    },

    onNavKey: function (e) {
      // Leave modified combos to the browser / second-screen handler.
      if (e.altKey || e.ctrlKey || e.metaKey) { return; }
      var el = e.target;
      if (el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable)) { return; }
      switch (e.key) {
        case 'j': case 'J': e.preventDefault(); this.onNext(); break;
        case 'k': case 'K': e.preventDefault(); this.onPrev(); break;
        case 'r': case 'R': e.preventDefault(); this.onReply(); break;
        default: return;
      }
    },

    onShortcutKey: function (e) {
      // Plain Alt only (Option on macOS); ignore other modifier combinations.
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) { return; }
      // Don't hijack Option+digit while the rep is typing (reply, notes, etc.).
      var el = e.target;
      if (el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable))
      {
        return;
      }

      var target;
      switch (e.key) {
        case 'Digit1': case 'Numpad1': case '1':  target = 'profile'; break;
        case 'Digit2': case 'Numpad2': case '2': target = 'offersList';  break;
        case 'Digit3': case 'Numpad3': case '3':  target = 'ordersList';  break;
        case 'Digit4': case 'Numpad4': case '4':  target = 'offersBoard';  break;
        case 'Digit5': case 'Numpad5': case '5':  target = 'ordersBoard';  break;
        // Company file isn't customer-scoped (keyed by companySecId, not the
        // end-user id), so it routes through its own push path.
        case 'Digit6': case 'Numpad6': case '6':  e.preventDefault(); this.pushCompanyToSecondScreen(); return;
        default: return;
      }
      e.preventDefault();
      this.pushCustomerToSecondScreen(target);
    },

    // Resolve the open item's linked customer and push the requested board /
    // profile to the second screen. No-op (with a brief on-screen hint) when
    // the item has no linked customer.
    pushCustomerToSecondScreen: function (target) {
      if (!target) { return; }
      // Any push needs a marked second-screen tab — tell the rep how to mark
      // one rather than silently dropping the shortcut.
      if (!SecondScreen.hasTarget()) {
        this.flashHint(_.i18n('salesInbox.secondScreen.noTarget', null,
          'No second-screen tab marked — click the screen icon in the header of the tab you want to control.'));
        return;
      }
      // Follow the customer selected in the switcher (falls back to the
      // email's linked customer).
      var endUserId = this.activeEndUserId || (this.contextData && this.contextData.customerEndUserId);
      if (endUserId) {
        SecondScreen.openCustomer(target, endUserId);
        return;
      }
      // No linked customer: the "profile" shortcut still helps by opening the
      // customers list on the second screen so the rep can find/create them.
      // The other targets (offers/orders/boards) are customer-scoped, so there
      // is nothing to show for them.
      if (target === 'profile') {
        SecondScreen.openCustomersList();
        return;
      }
      this.flashNoCustomer();
    },

    // Push the linked company's file to the second screen. Like the
    // customer-scoped pushes, it needs a marked target tab and a resolved
    // company (the company card only shows its launcher once one is linked).
    pushCompanyToSecondScreen: function () {
      if (!SecondScreen.hasTarget()) {
        this.flashHint(_.i18n('salesInbox.secondScreen.noTarget', null,
          'No second-screen tab marked — click the screen icon in the header of the tab you want to control.'));
        return;
      }
      var companySecId = this.companyData && this.companyData.secId;
      if (companySecId) { SecondScreen.openCompany(companySecId); }
    },

    flashNoCustomer: function () {
      var $line = this.$('.custline');
      if (!$line.length) { return; }
      $line.addClass('no-customer-flash');
      setTimeout(function () { $line.removeClass('no-customer-flash'); }, 1200);
    },

    // Transient one-line note in the detail header (e.g. "no target tab").
    flashHint: function (msg) {
      var $hdr = this.$('.detail-header');
      if (!$hdr.length) { return; }
      var $hint = $hdr.find('.second-screen-hint');
      if (!$hint.length) {
        $hint = $('<div class="second-screen-hint"></div>');
        $hdr.append($hint);
      }
      $hint.text(msg).addClass('show');
      clearTimeout(this._hintTimer);
      this._hintTimer = setTimeout(function () { $hint.removeClass('show'); }, 3000);
    },

    serializeData: function () {
      var d = _.extend({
        secId: '',
        subject: '',
        senderEmail: '',
        customerName: '',
        customerVip: false,
        receivedAt: null,
        emailMessageIdHeader: '',
        emailAddressSecId: '',
        category: null,
        emailCase: null,
        locale: null,
        potential: null,
        potentialValue: 0,
        latestOfferValue: null,
        customerValue: null,
        emailExtractedValue: null,
        priorityScore: 0,
        status: 'NEW',
        assignedUserpidName: '',
        assignedGroupName: '',
        classificationConfidence: null,
        manualOverride: false
      }, this.itemData || {});

      d.priorityScore = Number(d.priorityScore) || 0;
      d.prioTier = prioTier(d.priorityScore);
      d.sla = slaGauge(d.receivedAt, d.potential);
      d.receivedLabel = d.receivedAt ? new Date(d.receivedAt).toLocaleString('fr-BE') : '';
      d.statuses = STATUSES;
      d.prettyStatus = prettyCase;
      return d;
    },

    onBeforeShow: function () { this.load(); },

    load: function () {
      var self = this;
      this.rendered = false;
      this.contextLoaded = false;

      // The item and context endpoints are independent server calls, so fire
      // them concurrently instead of chaining context after the item. The
      // item drives the header / classification / activity panels; the
      // context (and the customer commands that depend on it) drives the
      // thread + commands panels. Running them in parallel removes a full
      // request round-trip from the time-to-interactive.
      this.loadContext();

      App.request('salesInbox:get', this.secId).done(function (data) {
        self.itemData = (data && data.item) || {};
        self.activityData = (data && data.activity) || [];
        self.threadItemsData = (data && data.threadItems) || [];
        self.render();
        self.renderClassificationPanel();
        self.renderActivityPanel();
        self.renderThreadItemsPanel();
        self.rendered = true;
        self.loadNav();
        if (self.contextLoaded) {
          // Context won the race while the item was loading — render the
          // real thread/commands panels straight away.
          self.applyContext();
        } else {
          // Still waiting on context — show placeholders for now; the
          // context response will swap them in via applyContext().
          self.renderThreadPanel();
          self.renderCommandsPanel();
        }
      }).fail(function () {
        self.$el.html('<div class="error">Could not load item.</div>');
      });
    },

    // --- Quick header actions --------------------------------------------
    // These mutate the item (status / assignee / team) but deliberately do NOT
    // call load(): a full reload re-renders the view and tears down the thread
    // region, which would wipe any reply/draft the operator is in the middle of
    // writing. Instead we patch just the affected header element + cached
    // itemData in place. The activity log isn't refreshed (it would need a
    // refetch); a manual reload / navigation picks up the new entry.
    onQuickStatus: function (e) {
      var self = this;
      var status = $(e.currentTarget).val();
      App.request('salesInbox:status', this.secId, status).done(function () {
        // Show the picked value immediately, then reconcile the rest of the
        // header (e.g. any server-side auto-reassignment) from fresh data.
        self.applyStatus(status);
        self.refreshTop();
      });
    },

    // Patch the status <select> + cached value (the select already shows the
    // chosen option; keep itemData authoritative for any later read).
    applyStatus: function (status) {
      this.itemData = this.itemData || {};
      this.itemData.status = status;
      this.$('.q-status').val(status);
    },

    // Patch the assignee chip + cached value without a reload.
    applyAssignee: function (secId, name) {
      this.itemData = this.itemData || {};
      this.itemData.assignedUserpidSecId = secId || null;
      this.itemData.assignedUserpidName = name || '';
      this.$('.btn-change-assignee').html(
        '<span class="k">Assignee</span> ' + _.escape(name || 'Unassigned') +
        ' <span class="pencil" aria-hidden="true">✎</span>'
      );
    },

    // Patch the team chip + cached value without a reload.
    applyTeam: function (secId, name) {
      this.itemData = this.itemData || {};
      this.itemData.assignedGroupSecId = secId || null;
      this.itemData.assignedGroupName = name || '';
      this.$('.btn-change-team').html(
        '<span class="k">Team</span> ' + _.escape(name || 'No team') +
        ' <span class="pencil" aria-hidden="true">✎</span>'
      );
    },

    // Refresh just the header (top part) + activity log from the server after a
    // status / assignee / team change — WITHOUT reloading the email thread, so
    // an in-progress reply/draft is preserved. We re-fetch the lightweight item
    // payload (item + activity, not the email bodies) rather than trust the
    // picker's model, whose assignee name attribute isn't always populated
    // (assign-to-me vs pick-from-list hand back different shapes). The status
    // may also auto-reassign server-side, which this picks up too.
    refreshTop: function () {
      var self = this;
      App.request('salesInbox:get', this.secId).done(function (data) {
        if (self.isDestroyed) { return; }
        var item = (data && data.item) || {};
        self.itemData = item;
        self.activityData = (data && data.activity) || self.activityData || [];
        self.applyStatus(item.status || 'NEW');
        self.applyAssignee(item.assignedUserpidSecId, item.assignedUserpidName);
        self.applyTeam(item.assignedGroupSecId, item.assignedGroupName);
        self.renderActivityPanel();
      });
    },

    // --- Prev/next navigation --------------------------------------------
    // Resolve the current item's neighbours within the list (using the list's
    // saved filters/order) and enable the header buttons accordingly. Uses a
    // module-level cache so walking the inbox doesn't refetch per hop.
    loadNav: function () {
      var self = this;
      var filters = readListFilters();
      var sig = JSON.stringify(filters || {});

      var apply = function (ids) {
        ids = ids || [];
        var idx = _.indexOf(ids, self.secId);
        self.prevSecId = idx > 0 ? ids[idx - 1] : null;
        self.nextSecId = (idx !== -1 && idx < ids.length - 1) ? ids[idx + 1] : null;
        self.navIndex = idx;
        self.navTotal = ids.length;
        self.applyNav();
      };

      if (navCache.sig === sig && navCache.ids) {
        apply(navCache.ids);
        return;
      }

      var params = App.request('salesInbox:filter-params', filters);
      params.first = 0;
      params.pageSize = NAV_WINDOW;

      $.ajax({
        type: 'GET',
        url: App.request('salesInbox:list-url', params)
      }).done(function (items) {
        var ids = _.map(items || [], function (it) { return it.secId; });
        navCache.sig = sig;
        navCache.ids = ids;
        apply(ids);
      }).fail(function () {
        apply([]);
      });
    },

    applyNav: function () {
      this.$('.btn-prev').prop('disabled', !this.prevSecId);
      this.$('.btn-next').prop('disabled', !this.nextSecId);
      // Queue position, e.g. "14 / 87" (total bounded by the nav window —
      // suffix a "+" when we may not have walked the whole list).
      var $pos = this.$('.pos');
      if (this.navIndex >= 0) {
        var total = this.navTotal >= NAV_WINDOW ? (this.navTotal + '+') : this.navTotal;
        $pos.text((this.navIndex + 1) + ' / ' + total);
      } else {
        $pos.text('');
      }
    },

    onPrev: function () {
      if (this.prevSecId) {
        App.navigate('sales-inbox/' + this.prevSecId, { trigger: true });
      }
    },

    onNext: function () {
      if (this.nextSecId) {
        App.navigate('sales-inbox/' + this.nextSecId, { trigger: true });
      }
    },

    // Copy the sender's email address to the clipboard. Uses the async
    // Clipboard API where available (HTTPS / localhost) and falls back to a
    // hidden-textarea + execCommand for older browsers or insecure contexts.
    // The button briefly flips to a checkmark to confirm.
    onCopyEmail: function (e) {
      e.preventDefault();
      e.stopPropagation();
      var $btn = $(e.currentTarget);
      var email = $btn.attr('data-email') || (this.itemData && this.itemData.senderEmail);
      if (!email) { return; }

      var self = this;
      var done = function (ok) { self.flashCopyResult($btn, ok); };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        // Two-arg then() rather than .catch() — the latter is a reserved word
        // as a member name under the project's jshint (es3) config.
        navigator.clipboard.writeText(email).then(
          function () { done(true); },
          function () { done(self.fallbackCopy(email)); }
        );
      } else {
        done(this.fallbackCopy(email));
      }
    },

    // Legacy clipboard copy via a transient textarea. Returns true on success.
    fallbackCopy: function (text) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      var ok = false;
      try {
        ok = document.execCommand('copy');
      } catch (err) {
        ok = false;
      }
      document.body.removeChild(ta);
      return ok;
    },

    flashCopyResult: function ($btn, ok) {
      $btn.addClass(ok ? 'copied' : 'copy-failed');
      $btn.attr('title', ok ? 'Copied!' : 'Copy failed');
      var original = $btn.html();
      $btn.html(ok ? '✓' : '⚠');
      setTimeout(function () {
        $btn.removeClass('copied copy-failed');
        $btn.attr('title', 'Copy email address');
        $btn.html(original);
      }, 1200);
    },

    onReply: function () {
      // The existing mails module handles composition + threading. Deep-link into
      // it with the current email's Message-ID so the user can compose a reply
      // against the correct thread. We don't try to re-implement composition here.
      var msgId = this.itemData && this.itemData.emailMessageIdHeader;
      var eaSec = this.itemData && this.itemData.emailAddressSecId;
      if (msgId && eaSec) {
        App.navigate('mails/' + eaSec + '/email/' + encodeURIComponent(msgId), { trigger: true });
      } else if (msgId) {
        App.navigate('mails//email/' + encodeURIComponent(msgId), { trigger: true });
      } else {
        App.navigate('mails', { trigger: true });
      }
    },

    /**
     * Open the shared UserSelectorView modal to reassign the inbox item.
     * Same component the orders module uses for its "Assign to" action, so
     * the UX is consistent across the dashboard. Single-select mode: the
     * first user the operator picks becomes the new assignee.
     *
     * UserSelectorView's `options.users` is actually the *pre-selected*
     * list (shown on the left panel); the candidate list on the right is
     * fetched internally by UsersView via `kanban-tickets:get-users`. We
     * only need to seed the current assignee so the modal opens showing
     * who the item is currently assigned to.
     */
    onChangeAssignee: function (e) {
      if (e) { e.preventDefault(); }
      var self = this;
      var item = self.itemData || {};
      // Patch the header chip in place (no reload) so an open reply/draft survives.
      this.openAssigneePicker(self.secId, item.assignedUserpidSecId, item.assignedUserpidName,
        function () { self.refreshTop(); });
    },

    /**
     * Open the shared UserSelectorView modal to (re)assign ANY sales-inbox item
     * by secId — used both for this item's header chip and for sibling items in
     * the "Same thread" panel. `onDone` runs after a successful assign/unassign
     * so the caller can refresh whatever view it owns.
     */
    openAssigneePicker: function (secId, currentSecId, currentName, onDone) {
      if (!secId) { return; }
      require(['userSelectorView'], function (UserSelectorView) {
        // Pre-select only the current assignee (if any). No need to fetch
        // the full user list ourselves — UsersView handles that.
        var preselected = [];
        if (currentSecId) {
          preselected.push(new Backbone.Model({
            secId: currentSecId,
            fullName: currentName || '',
            // `email` is used by UsersView's filter helper; leave blank
            // when we don't have it — the row still shows the name.
            email: ''
          }));
        }

        UserSelectorView.prototype.open({
          users: preselected,
          multiselect: false,
          onSelected: function (user) {
            // 'salesInbox:assign-user' only touches the user side, so the
            // current team assignment is preserved.
            var defer = $.Deferred();
            App.request('salesInbox:assign-user', secId, user.get('secId')).done(function () {
              if (onDone) { onDone(user); }
              defer.resolve();
            }).fail(function () { defer.reject(); });
            return defer.promise();
          },
          onUnSelected: function () {
            // Clicking the currently-selected user unassigns the user only.
            var defer = $.Deferred();
            App.request('salesInbox:assign-user', secId, null).done(function () {
              if (onDone) { onDone(null); }
              defer.resolve();
            }).fail(function () { defer.reject(); });
            return defer.promise();
          }
        });
      });
    },

    /**
     * Open a picker to reassign the team (UserpidGroup). We fetch the User
     * Groups list (same data the admin User Groups page consumes) and feed it
     * to the shared autocomplete component so the operator can type to filter
     * instead of scrolling a long list. Selecting a group → assign-group
     * (preserves the user assignee); clearing the field → "No team".
     */
    onChangeTeam: function (e) {
      if (e) { e.preventDefault(); }
      var self = this;
      var item = self.itemData || {};
      var currentGroupSecId = item.assignedGroupSecId || null;
      var currentGroupName = item.assignedGroupName || null;

      $.ajax({
        type: 'GET',
        url: App.request('userGroups:get-url', { first: 0, pageSize: 200 })
      }).done(function (groups) {
        groups = groups || [];
        require(['windows', 'marionette', 'autocompleteView'], function (Windows, Marionette, AutocompleteView) {
          // Apply the picked group (or null for "No team"). Idempotent: picking
          // the current team just closes the modal. Patches the chip in place
          // (no reload) so an open reply/draft survives.
          var assign = function (picked, pickedName) {
            if ((picked || null) === currentGroupSecId) {
              if (Windows.modalView) { Windows.modalView.close(); }
              return;
            }
            App.request('salesInbox:assign-group', self.secId, picked || null).done(function () {
              // Optimistic chip update, then reconcile the header from the server.
              self.applyTeam(picked || null, pickedName || '');
              self.refreshTop();
              if (Windows.modalView) { Windows.modalView.close(); }
            });
          };

          var PickerView = Marionette.LayoutView.extend({
            className: 'sales-inbox-team-picker',
            template: _.template(
              '<label class="team-picker-label">Team</label>' +
              '<div class="team-ac-region"></div>' +
              '<button type="button" class="btn-no-team">No team</button>'
            ),
            regions: { ac: '.team-ac-region' },
            events: { 'click .btn-no-team': 'onNoTeam' },
            onShow: function () {
              var acView = new AutocompleteView({
                type: 'dataset',
                data: groups,
                limit: 8,
                valueKey: 'name',
                apiKey: 'secId',
                placeholder: 'Search team…',
                value: currentGroupName,
                name: 'team',
                callback: function (field, model) {
                  // model is a Backbone.Model of the picked group, or null
                  // when the field was cleared.
                  assign(model ? (model.get('secId') || null) : null,
                         model ? (model.get('name') || '') : '');
                }
              });
              this.getRegion('ac').show(acView);
            },
            onNoTeam: function () { assign(null, ''); }
          });

          Windows.open({
            view: function () { return new PickerView(); },
            id: 'sales-inbox-team-picker',
            title: 'Assign team',
            modal: true,
            width: 360,
            height: 360
          });
        });
      });
    },

    // --- Context (thread + customer commands) ----------------------------
    // Fetches the thread context. Fired in parallel with the item load (and
    // again by refresh()). The context-dependent panels are rendered via
    // applyContext(), which waits until the main view has rendered so its
    // regions exist.
    loadContext: function () {
      var self = this;
      App.request('salesInbox:context', this.secId).done(function (ctx) {
        self.contextData = ctx || {};
        self.contextLoaded = true;
        self.resetActiveCustomer();
        self.applyContext();
      }).fail(function () {
        self.contextData = {};
        self.contextLoaded = true;
        self.resetActiveCustomer();
        self.applyContext();
      });
    },

    // The "active customer" is whose offers/orders the sidebar shows. It
    // starts as the email's linked customer and can be switched to another
    // sub-account of the same company via the dropdown (loadCompanyCustomers).
    resetActiveCustomer: function () {
      var ctx = this.contextData || {};
      this.activeEndUserId = ctx.customerEndUserId || null;
      this.activeCustomerName = ctx.customerName || '';
      this.companyCustomers = null;
    },

    // Render the context-dependent panels (thread + customer commands).
    // No-op until the main view has rendered: if the context request wins
    // the race against the item request, the item's done handler calls this
    // once the regions exist.
    applyContext: function () {
      if (!this.rendered) { return; }
      this.renderThreadPanel();
      this.renderCommandsPanel();
      this.loadCustomerCommands();
      this.loadCompany();
      this.applyEmailPopupBtn();
    },

    // --- Company information ----------------------------------------------
    // Show the company linked to this email's customer, using the SAME data the
    // order/offer header shows (see companies/common/link/view.js): the company
    // fields are flattened onto the EndUser payload — companySecId / companyName
    // / companyOrderAmount / companyNbrEndUsers — so we just read them off the
    // fetched customer, no separate company request. Loading and "nothing
    // linked" states are surfaced explicitly.
    loadCompany: function () {
      var self = this;
      var endUserId = this.contextData && this.contextData.customerEndUserId;

      this.companyData = null;
      this.customerLinked = !!endUserId;

      if (!endUserId) {
        this.renderCompanyPanel(false);
        return;
      }

      this.renderCompanyPanel(true);
      var customer = App.request('customers:entity:end-user-id', endUserId);
      customer.fetch()
        .done(function () {
          if (self.isDestroyed) { return; }
          self.companyData = {
            secId: customer.get('companySecId') || null,
            name: customer.get('companyName') || '',
            orderAmount: customer.get('companyOrderAmount'),
            nbrEndUsers: customer.get('companyNbrEndUsers')
          };
          self.renderCompanyPanel(false);
          // Load the company's sub-accounts so the Customer card can offer a
          // switcher. We don't gate on nbrEndUsers (not always populated on
          // this endpoint) — the fetch is authoritative and cheap, and the
          // switcher only shows when it actually returns more than one.
          if (self.companyData.secId) {
            self.loadCompanyCustomers(self.companyData.secId);
          }
        })
        .fail(function () {
          if (self.isDestroyed) { return; }
          self.renderCompanyPanel(false);
        });
    },

    // Fetch the company's sub-accounts (EndUsers) so the Customer card can
    // switch between them. Best-effort: a failure just leaves the switcher
    // hidden (the email's own customer is still shown).
    loadCompanyCustomers: function (companySecId) {
      var self = this;
      var company = App.request('companies:entity', companySecId);
      // companies:entity only sets the model's `url`; getEndUsers() builds its
      // own URL from the `secId` attribute, so set it or the request hits
      // companies.json/undefined/endusers and comes back empty.
      company.set('secId', companySecId);
      company.getEndUsers().done(function (items) {
        if (self.isDestroyed) { return; }
        var customers = _.chain(items || [])
          .map(function (it) {
            var endUserId = it.endUserId || it.code;
            if (!endUserId) { return null; }
            var name = it.name ||
              $.trim((it.firstName || '') + ' ' + (it.lastName || '')) ||
              it.email || endUserId;
            return { endUserId: endUserId, name: name, email: it.email || '' };
          })
          .compact()
          .value();
        // Only useful as a switcher when there's a real choice; make sure the
        // currently-active customer is present so it can stay selected.
        var hasActive = _.some(customers, function (c) { return c.endUserId === self.activeEndUserId; });
        if (!hasActive && self.activeEndUserId) {
          customers.unshift({ endUserId: self.activeEndUserId, name: self.activeCustomerName || self.activeEndUserId, email: '' });
        }
        self.companyCustomers = customers.length > 1 ? customers : null;
        if (self.companyCustomers) { self.renderCommandsPanel(); }
      });
    },

    // Switch the Customer card to another sub-account: reload its offers /
    // orders and point the second-screen shortcuts at it. The email's thread
    // is untouched — only the customer history view changes.
    switchCustomer: function (endUserId) {
      if (!endUserId || endUserId === this.activeEndUserId) { return; }
      var picked = _.find(this.companyCustomers || [], function (c) { return c.endUserId === endUserId; });
      this.activeEndUserId = endUserId;
      this.activeCustomerName = picked ? picked.name : '';
      // Reset to the loading state, then fetch the selected customer's history.
      this.offersCollection = null;
      this.ordersCollection = null;
      this.renderCommandsPanel();
      this.loadCustomerCommands();
    },

    renderCompanyPanel: function (loading) {
      var region = this.getRegion('company');
      if (!region) { return; }

      var self = this;
      var c = this.companyData || {};
      var customerLinked = !!this.customerLinked;
      var hasCompany = !!c.secId;
      // Advanced-mode only, mirroring the customer card's second-screen
      // launchers — pushes the company file to the marked second-screen tab.
      var secondScreen = Settings.configValue('advancedMode') === true;

      var PanelView = Marionette.ItemView.extend({
        className: 'company-card',
        events: {
          'click .open-2screen': function () { self.pushCompanyToSecondScreen(); }
        },
        template: _.template(
          '<div class="chead"><h3>Company information</h3>' +
          '  <% if (secId) { %><span class="chead-actions">' +
          '    <% if (secondScreen) { %>' +
          '    <button type="button" class="open-2screen" title="Open company on the second screen (Alt+6)">' +
          '      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
          '        <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>' +
          '      <span class="kbd">⌥6</span></button><% } %>' +
          '    <a class="open-cust" href="#companies/<%- encodeURIComponent(secId) %>" title="Open company file">open ↗</a>' +
          '  </span><% } %>' +
          '</div>' +
          '<% if (loading) { %>' +
          '  <div class="empty">Loading…</div>' +
          '<% } else if (!customerLinked) { %>' +
          '  <div class="empty">No customer linked to this email.</div>' +
          '<% } else if (!hasCompany) { %>' +
          '  <div class="empty">No company linked to this customer.</div>' +
          '<% } else { %>' +
          '  <div class="co-name"><%- name || "Unknown company" %></div>' +
          '  <div class="co-stats">' +
          '    <div class="stat"><span class="lab"><%- customersLabel %></span><span class="val"><%- nbrEndUsers %></span></div>' +
          '    <div class="stat"><span class="lab"><%- amountLabel %></span><span class="val"><%- orderAmount %> €</span></div>' +
          '  </div>' +
          '<% } %>'
        ),
        serializeData: function () {
          var amount = (c.orderAmount === null || c.orderAmount === undefined) ? 0 : Number(c.orderAmount);
          var nbr = (c.nbrEndUsers === null || c.nbrEndUsers === undefined) ? 0 : c.nbrEndUsers;
          return {
            loading: !!loading,
            customerLinked: customerLinked,
            hasCompany: hasCompany,
            secondScreen: secondScreen,
            secId: c.secId || '',
            name: c.name || '',
            orderAmount: (Number(amount) || 0).toFixed(2),
            nbrEndUsers: nbr,
            // Same i18n keys the order/offer header uses.
            amountLabel: _.i18n('companies.orderAmount', null, 'Orders total'),
            customersLabel: _.i18n('companies.nbrEndUsers', null, 'Sub-accounts')
          };
        }
      });

      region.show(new PanelView());
    },

    // Enable the "Open email" button only once we have a resolvable thread
    // (same prerequisites as the inline thread panel).
    applyEmailPopupBtn: function () {
      var ctx = this.contextData || {};
      this.$('.btn-open-email').prop('disabled', !(ctx.threadId && ctx.emailAddressSecId));
    },

    // Open the email conversation in a large modal for a roomier read. Reuses
    // the same Threads view the inline panel renders (so messages, attachments,
    // labels and the reply composer behave identically) — just in a popup.
    onOpenEmailPopup: function () {
      var self = this;
      var ctx = this.contextData || {};
      if (!ctx.threadId || !ctx.emailAddressSecId) { return; }

      require(['windows', 'threadsThreadView'], function (Windows, ThreadView) {
        var subject = ctx.subject ||
          (self.itemData && (self.itemData.subject || self.itemData.emailMessageIdHeader)) || 'Email';
        Windows.open({
          view: function () {
            var threadModel = App.request('threads:model', {
              threadId: ctx.threadId,
              subject: subject
            });
            return new ThreadView({
              model: threadModel,
              mailSecId: ctx.emailAddressSecId,
              scrollToLatest: true,
              collapsibleReply: true,
              highlightMessageId: self.itemData && self.itemData.emailMessageIdHeader,
              templateLocale: self.itemData && self.itemData.locale,
              customerLinked: !!(ctx && ctx.customerEndUserId),
              customerEndUserId: ctx && ctx.customerEndUserId,
              // The thread view's "back" closes the popup instead of emptying
              // it; the modal's own close button does the same.
              parentView: { refresh: function () { if (Windows.modalView) { Windows.modalView.close(); } } }
            });
          },
          id: 'sales-inbox-email:' + self.secId,
          title: subject,
          modal: true,
          width: '80%',
          height: '88%'
        });
      });
    },

    // --- Mailbox search panel (left) -------------------------------------
    // A magnifier rail toggles a left panel that searches the mailbox's whole
    // "ALL MAILS" folder for the customer's email address, listing every past
    // email with them (received or sent). A result opens in the same thread
    // popup the "Open ↗" button uses.
    onToggleMailSearch: function () {
      var $body = this.$('.detail-body');
      var opening = !$body.hasClass('mail-search-open');
      $body.toggleClass('mail-search-open', opening);
      this.$('.mail-search-toggle').toggleClass('active', opening);
      if (!opening) { return; }
      var ctx = this.contextData || {};
      var email = ctx.customerEmail || (this.itemData && this.itemData.senderEmail) || '';
      var region = this.getRegion('mailSearch');
      // (Re)search once per resolved email — reopening reuses the results, but
      // a context that resolved an email after the first open re-renders.
      if (region && (this._mailSearchEmail !== email || !region.currentView)) {
        this._mailSearchEmail = email;
        this.renderMailSearchPanel();
      }
    },

    closeMailSearch: function () {
      this.$('.detail-body').removeClass('mail-search-open');
      this.$('.mail-search-toggle').removeClass('active');
    },

    renderMailSearchPanel: function () {
      var self = this;
      var region = this.getRegion('mailSearch');
      if (!region) { return; }
      var ctx = this.contextData || {};
      var email = ctx.customerEmail || (this.itemData && this.itemData.senderEmail) || '';
      var eaSec = ctx.emailAddressSecId || (this.itemData && this.itemData.emailAddressSecId) || '';

      var PanelView = Marionette.ItemView.extend({
        className: 'mail-search-card',
        template: _.template(
          '<div class="ms-head"><h3>Customer mailbox</h3>' +
          '  <button type="button" class="ms-close" title="Close">&times;</button></div>' +
          '<form class="ms-form">' +
          '  <input type="text" class="ms-input" placeholder="Search the mailbox…" value="<%= esc(email) %>"/>' +
          '  <button type="submit" class="ms-search-btn" title="Search" aria-label="Search">' +
          '    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>' +
          '  </button>' +
          '</form>' +
          '<div class="ms-results"><div class="ms-loading">Searching…</div></div>'
        ),
        templateHelpers: function () {
          return { email: email, esc: function (s) { return escapeHtml(s); } };
        },
        events: {
          'click .ms-close': function () { self.closeMailSearch(); },
          'submit .ms-form': 'onSubmit',
          'click .ms-row': 'onRowClick'
        },
        onSubmit: function (e) {
          e.preventDefault();
          this.search();
        },
        // Search whatever is in the box. It defaults to the customer's email
        // (pre-filled), but the operator can type any Gmail query / address.
        search: function () {
          var view = this;
          var query = $.trim(this.$('.ms-input').val() || '');
          if (!eaSec) {
            this.$('.ms-results').html('<div class="ms-empty">No mailbox to search.</div>');
            return;
          }
          if (!query) {
            this.$('.ms-results').html('<div class="ms-empty">Type something to search.</div>');
            return;
          }
          this.$('.ms-results').html('<div class="ms-loading">Searching…</div>');
          // entities/mails registers the mails:get-url handler.
          require(['entities/mails'], function () {
            $.ajax({
              url: App.request('mails:get-url', eaSec, {
                folder: 'ALL MAILS',
                search: query,
                page: 0,
                size: 30,
                sidx: 'sentDate',
                sort: 'sentDate,desc'
              })
            }).done(function (resp) {
              if (view.isDestroyed) { return; }
              view.renderResults((resp && resp.content) || []);
            }).fail(function () {
              if (view.isDestroyed) { return; }
              view.$('.ms-results').html('<div class="ms-empty">Search failed.</div>');
            });
          });
        },
        renderResults: function (items) {
          if (!items.length) {
            this.$('.ms-results').html('<div class="ms-empty">No emails found.</div>');
            return;
          }
          var rows = _.map(items, function (m) {
            var from = (m.from && m.from[0]) || {};
            var name = escapeHtml(from.personal || from.address || '(unknown)');
            var subj = escapeHtml(m.subject || '(no subject)');
            var date = m.sentDate ? new Date(m.sentDate).toLocaleDateString('fr-BE',
              { day: '2-digit', month: '2-digit', year: '2-digit' }) : '';
            var clip = m.hasAttachments ? '<span class="ms-clip" title="Has attachments">📎</span>' : '';
            var draft = m.draft ? '<span class="ms-draft" title="Unsent draft">Draft</span>' : '';
            return '<li class="ms-row' + (m.draft ? ' is-draft' : '') + '" data-id="' + escapeHtml(m.id || '') + '">' +
              '<div class="ms-row-top"><span class="ms-from">' + name + '</span>' + draft + clip +
              '<span class="ms-date">' + date + '</span></div>' +
              '<div class="ms-subj">' + subj + '</div></li>';
          }).join('');
          this.$('.ms-results').html('<ul class="ms-list">' + rows + '</ul>');
        },
        onRowClick: function (e) {
          var id = $(e.currentTarget).attr('data-id');
          if (id) { self.openMailSearchResult(eaSec, id); }
        }
      });

      // Show then kick off the search explicitly — region.show renders & attaches
      // the view synchronously, and we don't rely on onShow firing (it doesn't
      // reliably for a region shown on a later user interaction).
      var panelView = new PanelView();
      region.show(panelView);
      panelView.search();
    },

    // Open a search result: fetch the full message (which includes its HTML
    // body) and render it directly in a modal. We render the body in a
    // sandboxed iframe so the email's markup/CSS can't bleed into or attack the
    // dashboard, and so it shows reliably regardless of thread state.
    openMailSearchResult: function (eaSec, msgId) {
      var self = this;
      require(['entities/mails', 'windows', 'marionette'], function (MailsEntities, Windows, Marionette) {
        $.ajax({ url: App.request('mails:get-mail-url', eaSec, 'ALL MAILS', msgId) })
          .done(function (full) {
            if (self.isDestroyed || !full) { return; }
            self.openMessagePopup(Windows, Marionette, eaSec, full);
          });
      });
    },

    openMessagePopup: function (Windows, Marionette, eaSec, full) {
      var subject = full.subject || '(no subject)';
      var fromAddr = (full.from && full.from[0]) || {};
      var fromTxt = fromAddr.personal ?
        (fromAddr.personal + ' <' + (fromAddr.address || '') + '>') :
        (fromAddr.address || '');
      var date = full.sentDate ? new Date(full.sentDate).toLocaleString('fr-BE') : '';
      var bodyHtml = full.content || full.contentText || '<em>No content.</em>';

      var fmtBytes = function (n) {
        n = Number(n) || 0;
        if (!n) { return ''; }
        if (n < 1024) { return n + ' B'; }
        if (n < 1048576) { return Math.round(n / 1024) + ' KB'; }
        return (n / 1048576).toFixed(1) + ' MB';
      };
      // Only hide attachments that are TRULY embedded in the body (an inline
      // image referenced by its cid, e.g. a signature logo). A real attachment
      // is shown even when the sender flagged it Content-Disposition: inline.
      var isEmbedded = function (a) {
        if (!a.inline || !a.contentId) { return false; }
        var cid = String(a.contentId).replace(/[<>]/g, '');
        return cid && bodyHtml.indexOf(cid) !== -1;
      };
      var attsHtml = _.map((full.attachments || []), function (a) {
        if (!a || !a.fileName || isEmbedded(a)) { return ''; }
        var url = Settings.url('compuzz2', 'mails/fetch/' + eaSec + '/' + (full.id || '') +
          '/download/' + encodeURIComponent(a.fileName));
        var sz = fmtBytes(a.size);
        return '<a href="' + url + '" target="_blank" rel="noopener" title="Download" ' +
          'style="display:inline-flex;align-items:center;gap:5px;max-width:240px;padding:4px 9px;' +
          'border:1px solid #d6dae0;border-radius:7px;background:#f7f8fa;color:#2563eb;' +
          'text-decoration:none;font-size:12px;">' +
          '<span>📎</span><span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' +
          escapeHtml(a.fileName) + '</span>' +
          (sz ? '<span style="color:#8a8f98;flex:0 0 auto;">' + sz + '</span>' : '') +
          '</a>';
      }).join('');

      var MsgView = Marionette.ItemView.extend({
        className: 'mail-search-message',
        template: _.template(
          '<div class="msm-head" style="padding:0 2px 10px;border-bottom:1px solid #e2e5e9;margin-bottom:10px;flex:0 0 auto;">' +
          '  <div style="font-size:15px;font-weight:700;color:#1a1a1a;"><%= subject %></div>' +
          '  <div style="font-size:12px;color:#5f6368;margin-top:4px;"><%= from %><% if (date) { %> · <%= date %><% } %></div>' +
          '</div>' +
          '<% if (atts) { %><div class="msm-atts" style="flex:0 0 auto;display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px;"><%= atts %></div><% } %>' +
          '<iframe class="msm-frame" sandbox="allow-same-origin" title="Email content" ' +
          'style="flex:1 1 auto;width:100%;border:0;min-height:420px;"></iframe>'
        ),
        templateHelpers: function () {
          return {
            subject: escapeHtml(subject),
            from: escapeHtml(fromTxt),
            date: escapeHtml(date),
            atts: attsHtml
          };
        },
        onRender: function () {
          this.$el.css({ display: 'flex', 'flex-direction': 'column', height: '100%' });
          var iframe = this.$('.msm-frame')[0];
          // Sandbox WITHOUT allow-scripts → the email's JS can't run (XSS-safe).
          if (iframe) { iframe.srcdoc = bodyHtml; }
        }
      });

      Windows.open({
        view: function () { return new MsgView(); },
        id: 'sales-inbox-mailmsg:' + (full.id || ''),
        title: subject,
        modal: true,
        width: '78%',
        height: '86%'
      });
    },

    // --- Thread panel ----------------------------------------------------
    // Reuses the existing Threads module view (threadsThreadView) so the
    // conversation UI, message rendering, attachment download, labels and
    // reply composer are identical to the dedicated "Threads" page. We only
    // need the Gmail threadId + the mailbox secId, which the /context
    // endpoint resolves server-side for us.
    renderThreadPanel: function () {
      var self = this;
      var region = this.getRegion('thread');
      if (!region) return;
      var ctx = this.contextData || {};

      if (!ctx.threadId || !ctx.emailAddressSecId) {
        var msg;
        if (!ctx.threadId && ctx.threadError) {
          if (ctx.threadError === 'mailbox_not_api_enabled') {
            msg = 'Mailbox is not API-enabled — thread view unavailable.';
          } else if (ctx.threadError === 'not_found') {
            msg = 'Email not found in mailbox (deleted or archived).';
          } else if (ctx.threadError === 'no_mailbox') {
            msg = 'No mailbox linked to this email.';
          } else {
            msg = 'Thread unavailable (' + _.escape(ctx.threadError) + ').';
          }
        } else {
          msg = 'Loading thread…';
        }
        region.show(new (Marionette.ItemView.extend({
          className: 'sales-inbox-thread-fallback',
          template: _.template('<div class="thread-fallback"><%= msg %></div>'),
          templateHelpers: function () { return { msg: msg }; }
        }))());
        return;
      }

      require(['threadsThreadView'], function (ThreadView) {
        var threadModel = App.request('threads:model', {
          threadId: ctx.threadId,
          subject: ctx.subject || self.itemData.subject || self.itemData.emailMessageIdHeader || ''
        });
        var view = new ThreadView({
          model: threadModel,
          mailSecId: ctx.emailAddressSecId,
          // Land on the most recent email instead of the top of the thread.
          scrollToLatest: true,
          // Minimise the reply composer by default — more room for the email.
          collapsibleReply: true,
          // Outline the message this item is linked to (its emailMessageIdHeader
          // is the Gmail message id, matching each thread message's `id`).
          highlightMessageId: self.itemData && self.itemData.emailMessageIdHeader,
          // Reply signature should match the email's locale when we know it.
          templateLocale: self.itemData && self.itemData.locale,
          // Customer's own language — drives the From sender address (falls
          // back to the email's locale when the customer has none).
          customerLocale: ctx && ctx.customerLocale,
          // customerEndUserId is the "a customer is linked" signal: when it's
          // absent the reply composer asks which signature language to use.
          customerLinked: !!(ctx && ctx.customerEndUserId),
          // Forwarded so the reply composer can load the customer and feed
          // customer.* variables into the signature template.
          customerEndUserId: ctx && ctx.customerEndUserId,
          parentView: self
        });
        region.show(view);
      });
    },

    // Threads view invokes this on its "back" button — just reload context.
    refresh: function () { this.loadContext(); },

    // --- Customer commands (offers + orders) -----------------------------
    // Fetches the customer's offers and orders via the shared
    // entities/customer_commands request handlers, which return proper
    // Backbone collections. We keep the resolved collections on the view so
    // renderCommandsPanel can hand them to the Marionette CompositeView
    // machinery defined in modules/pages/sales_inbox/detail/commands/.
    loadCustomerCommands: function () {
      var self = this;
      var endUserId = this.activeEndUserId;
      if (!endUserId) {
        this.offersCollection = App.request('customerCommands:collection', []);
        this.ordersCollection = App.request('customerCommands:collection', []);
        this.renderCommandsPanel();
        return;
      }

      $.when(
        App.request('customerCommands:offers', endUserId),
        App.request('customerCommands:orders', endUserId)
      ).done(function (offersCollection, ordersCollection) {
        self.offersCollection = offersCollection;
        self.ordersCollection = ordersCollection;
        self.renderCommandsPanel();
      }).fail(function () {
        self.offersCollection = App.request('customerCommands:collection', []);
        self.ordersCollection = App.request('customerCommands:collection', []);
        self.renderCommandsPanel();
      });
    },

    renderCommandsPanel: function () {
      var region = this.getRegion('commands');
      if (!region) { return; }
      var ctx = this.contextData || {};
      var loading = !this.offersCollection || !this.ordersCollection;

      var item = this.itemData || {};
      // VIP / lifetime are the email customer's figures — only meaningful while
      // the email's own customer is the one selected.
      var isEmailCustomer = this.activeEndUserId === ctx.customerEndUserId;
      region.show(new CommandsPanelView({
        customerName: this.activeCustomerName || ctx.customerName,
        endUserId: this.activeEndUserId,
        customerVip: isEmailCustomer && !!item.customerVip,
        // Switcher: the company's sub-accounts (only rendered when >1).
        customers: this.companyCustomers,
        activeEndUserId: this.activeEndUserId,
        onSwitchCustomer: _.bind(this.switchCustomer, this),
        // Money context for the value wells (from the item payload).
        customerValue: Number(item.customerValue) || 0,
        emailValue: Number(item.potentialValue) || 0,
        // Second-screen launcher buttons are advanced-mode only.
        secondScreen: Settings.configValue('advancedMode') === true,
        loading: loading,
        offersCollection: this.offersCollection,
        ordersCollection: this.ordersCollection,
        // A status change that's configured to send a templated mail loads it
        // into the inline reply composer instead of opening the popup.
        onComposeTemplate: _.bind(this.composeTemplateInReply, this)
      }));
    },

    // Load a status-change mail template into the thread's live composer.
    // Returns true when it was handed off; false (so the caller can fall back
    // to the EmailView popup) when there's no live composer — e.g. the thread
    // is unavailable or still loading. Works whether the composer region is
    // hosting the reply view or a saved-draft view (both expose loadTemplate).
    composeTemplateInReply: function (emailOptions) {
      var region = this.getRegion('thread');
      var threadView = region && region.currentView;
      if (!threadView || threadView.isDestroyed) { return false; }
      // The thread shows either the reply composer or the draft composer in the
      // same region (and keeps a stale reference to whichever isn't live), so
      // hand the template to whatever is actually mounted there.
      var replyRegion = threadView.getRegion && threadView.getRegion('replyRegion');
      var composer = replyRegion && replyRegion.currentView;
      if (!composer || composer.isDestroyed ||
          typeof composer.loadTemplate !== 'function') {
        return false;
      }
      composer.loadTemplate(emailOptions);
      return true;
    },

    // --- Classification + activity (compact in sidebar) ------------------
    renderClassificationPanel: function () {
      var self = this;
      var item = this.itemData || {};
      var region = this.getRegion('classification');
      if (!region) return;

      // Most items are already auto-classified correctly, so the card shows
      // the current classification as compact chips and keeps the editing
      // form behind an "edit" toggle — prime sidebar space isn't spent on a
      // four-select form that's rarely touched.
      var potShort = function (p) {
        return p ? prettyCase(String(p).replace(/_ORDER_VALUE$/, '')) : '—';
      };

      var PanelView = Marionette.ItemView.extend({
        className: 'classification-card',
        template: _.template(
          '<div class="chead"><h3>Classification</h3>' +
          '  <button type="button" class="edit btn-edit-class">edit ✎</button>' +
          '</div>' +
          '<div class="classify-chips">' +
          '  <span class="cl"><span class="k">Cat</span><span class="v"><%= item.category ? pretty(item.category) : "—" %></span></span>' +
          '  <span class="cl"><span class="k">Case</span><span class="v"><%= item.emailCase ? pretty(item.emailCase) : "—" %></span></span>' +
          '  <span class="cl"><span class="k">Value</span><span class="v"><%= potShort(item.potential) %></span></span>' +
          '  <span class="cl"><span class="k">Lang</span><span class="v"><%= item.locale ? item.locale.toUpperCase() : "—" %></span></span>' +
          '</div>' +
          '<div class="classify-edit" hidden>' +
          '  <div class="row"><label>Category</label>' +
          '    <select class="f-category">' +
          '      <option value="">—</option>' +
          '      <% _.each(categories, function(c) { %>' +
          '        <option value="<%= c %>" <%= item.category === c ? "selected" : "" %>><%= pretty(c) %></option>' +
          '      <% }); %>' +
          '    </select>' +
          '  </div>' +
          '  <div class="row"><label>Case</label>' +
          '    <select class="f-case">' +
          '      <option value="">—</option>' +
          '      <% _.each((cases[item.category] || []), function(c) { %>' +
          '        <option value="<%= c %>" <%= item.emailCase === c ? "selected" : "" %>><%= pretty(c) %></option>' +
          '      <% }); %>' +
          '    </select>' +
          '  </div>' +
          '  <div class="row"><label>Potential</label>' +
          '    <select class="f-potential">' +
          '      <% _.each(potentials, function(p) { %>' +
          '        <option value="<%= p %>" <%= item.potential === p ? "selected" : "" %>><%= potShort(p) %></option>' +
          '      <% }); %>' +
          '    </select>' +
          '  </div>' +
          '  <div class="row"><label>Locale</label>' +
          '    <select class="f-locale">' +
          '      <% if (!item.locale || locales.indexOf(item.locale) < 0) { %>' +
          '        <option value="" selected><%= item.locale ? item.locale.toUpperCase() : "—" %></option>' +
          '      <% } %>' +
          '      <% _.each(locales, function(l) { %>' +
          '        <option value="<%= l %>" <%= item.locale === l ? "selected" : "" %>><%= l.toUpperCase() %></option>' +
          '      <% }); %>' +
          '    </select>' +
          '  </div>' +
          '  <div class="actions"><button class="btn btn-save">Save classification</button></div>' +
          '</div>' +
          '<div class="note">' +
          '  <textarea placeholder="Add a note…"></textarea>' +
          '  <button class="btn btn-note">Add note</button>' +
          '</div>'
        ),

        templateHelpers: function () {
          return {
            item: item,
            pretty: prettyCase,
            potShort: potShort,
            categories: CATEGORIES,
            cases: CASES,
            potentials: POTENTIALS,
            locales: LOCALES
          };
        },

        events: {
          'click .btn-edit-class': 'onToggleEdit',
          'change .f-category': 'onCategoryChange',
          'change .f-locale': 'onLocaleChange',
          'click .btn-save': 'onSave',
          'click .btn-note': 'onAddNote'
        },

        onToggleEdit: function () {
          var $edit = this.$('.classify-edit');
          var hidden = $edit.prop('hidden');
          $edit.prop('hidden', !hidden);
          this.$('.classify-chips').prop('hidden', hidden);
          this.$('.btn-edit-class').text(hidden ? 'done' : 'edit ✎');
        },

        onLocaleChange: function (e) {
          var locale = $(e.currentTarget).val();
          if (!locale) return;
          App.request('salesInbox:set-locale', self.secId, locale).done(function () { self.load(); });
        },

        onCategoryChange: function (e) {
          var cat = $(e.currentTarget).val();
          var $case = this.$('.f-case');
          $case.empty().append('<option value="">—</option>');
          _.each(CASES[cat] || [], function (c) {
            $case.append('<option value="' + c + '">' + c + '</option>');
          });
        },

        onSave: function () {
          var payload = {
            category: this.$('.f-category').val() || null,
            emailCase: this.$('.f-case').val() || null,
            potential: this.$('.f-potential').val() || null
          };
          App.request('salesInbox:reclassify', self.secId, payload).done(function () { self.load(); });
        },

        onAddNote: function () {
          var text = this.$('textarea').val();
          if (!text) return;
          App.request('salesInbox:note', self.secId, text).done(function () {
            self.$('textarea').val('');
            self.load();
          });
        }
      });

      region.show(new PanelView());
    },

    // Other still-open sales-inbox items on the same email thread. Lets the
    // agent jump to related work (e.g. a sibling design/reclamation item)
    // without losing context. Hidden entirely when there are none.
    renderThreadItemsPanel: function () {
      var region = this.getRegion('threadItems');
      if (!region) { return; }
      var items = this.threadItemsData || [];
      if (!items.length) {
        region.empty();
        return;
      }

      var parent = this;
      var PanelView = Marionette.ItemView.extend({
        className: 'thread-items-card',
        template: _.template(
          '<div class="chead"><h3>Same thread</h3><span class="count"><%= items.length %></span></div>' +
          '<ul class="ti-list">' +
          '  <% _.each(items, function(it) { %>' +
          '    <li class="ti-row" data-secid="<%= it.secId %>" title="Open this item">' +
          '      <span class="ti-status s-<%= (it.status || "").toLowerCase() %>"><%= prettyCase(it.status) %></span>' +
          '      <div class="ti-body">' +
          '        <div class="ti-subj"><%= subj(it) %></div>' +
          '        <div class="ti-meta">' +
          '          <% if (it.category) { %><span class="ti-cat"><%= it.category %></span> · <% } %>' +
          '          <span class="ti-assignee"><%= it.assignedUserpidName || "Unassigned" %></span>' +
          '        </div>' +
          '      </div>' +
          '      <button type="button" class="ti-assign" title="Assign this item"' +
          '        data-secid="<%= it.secId %>"' +
          '        data-assignee-secid="<%= esc(it.assignedUserpidSecId || "") %>"' +
          '        data-assignee-name="<%= esc(it.assignedUserpidName || "") %>">Assign</button>' +
          '    </li>' +
          '  <% }); %>' +
          '</ul>'
        ),
        templateHelpers: function () {
          return {
            items: items,
            prettyCase: function (s) { return prettyCase(s); },
            esc: function (s) { return escapeHtml(s); },
            subj: function (it) {
              return escapeHtml(it.subject || it.senderEmail || it.emailMessageIdHeader || '(no subject)');
            }
          };
        },
        events: {
          'click .ti-assign': 'onAssign',
          'click .ti-row': 'onRowClick'
        },
        // The assign button sits inside the row (whose click navigates), so
        // stop the bubble — assigning must not also open the item.
        onAssign: function (e) {
          e.stopPropagation();
          var $btn = $(e.currentTarget);
          var secId = $btn.attr('data-secid');
          parent.openAssigneePicker(
            secId,
            $btn.attr('data-assignee-secid') || null,
            $btn.attr('data-assignee-name') || null,
            function () { parent.reloadThreadItems(); }
          );
        },
        onRowClick: function (e) {
          var secId = $(e.currentTarget).attr('data-secid');
          if (secId) { App.navigate('sales-inbox/' + secId, { trigger: true }); }
        }
      });

      region.show(new PanelView());
    },

    // Re-fetch just the thread-items data and re-render the panel — used after
    // assigning a sibling item so its assignee reflects the change without
    // tearing down the rest of the detail view (thread/reply stay intact).
    reloadThreadItems: function () {
      var self = this;
      App.request('salesInbox:get', this.secId).done(function (data) {
        if (self.isDestroyed) { return; }
        self.threadItemsData = (data && data.threadItems) || [];
        self.renderThreadItemsPanel();
      });
    },

    renderActivityPanel: function () {
      var region = this.getRegion('activity');
      if (!region) return;
      var activity = this.activityData || [];

      var entries = _.map(activity, function (e) {
        return _.extend({}, e, {
          _parsed: parseActivityDetails(e.detailsJson),
          _when: e.actionAt ? new Date(e.actionAt) : null
        });
      });
      entries = _.map(entries, function (e) {
        var summary = summariseActivity(e);
        return _.extend({}, e, summary, {
          _relative: e._when ? relativeTime(e._when) : '',
          _absolute: e._when ? e._when.toLocaleString('fr-BE') : ''
        });
      });

      var PanelView = Marionette.ItemView.extend({
        className: 'activity-card',
        template: _.template(
          '<div class="chead"><h3>Activity</h3><span class="count"><%= entries.length %></span></div>' +
          '<div class="timeline">' +
          '  <% if (!entries.length) { %><div class="empty">No activity yet.</div><% } %>' +
          '  <% _.each(entries, function(e) { %>' +
          '    <div class="tl g-<%= e.kind %>">' +
          '      <div class="glyph" title="<%= e.action %>"><%= glyph(e.kind) %></div>' +
          '      <div class="tbody">' +
          '        <div class="ttl"><%= e.title %></div>' +
          '        <% if (e.subtitle) { %><div class="tsub"><%= e.subtitle %></div><% } %>' +
          '        <div class="tmeta">' +
          '          <% if (e.userpidName) { %><span class="twho"><%= e.userpidName %></span> · <% } %>' +
          '          <span class="twhen" title="<%= e._absolute %>"><%= e._relative %></span>' +
          '        </div>' +
          '      </div>' +
          '    </div>' +
          '  <% }); %>' +
          '</div>'
        ),
        templateHelpers: function () {
          return {
            entries: entries,
            glyph: function (kind) { return KIND_GLYPH[kind] || KIND_GLYPH.generic; }
          };
        }
      });

      region.show(new PanelView());
    }
  });
});

